import { ProductInboundTransactionDoc, ProductInventoryDoc, ProductInventoryUnit } from '@gooduncles/gu-app-schema';
import { chunk } from 'lodash-es';
import useSWRImmutable from 'swr/immutable';

import { getProductInboundTransactions, getProductInventories } from 'src/utils/firebase/firebase-short-cut';

export const FIRESTORE_IN_QUERY_LIMIT = 30;

/** 입고 처리 목록에 등장하는 재고를 id로 찾을 수 있게 모아준다. */
export const fetchInventoryById = async (transactions: ProductInboundTransactionDoc[]) => {
  const inventoryIds = Array.from(new Set(transactions.map((transaction) => transaction.inventoryId)));
  return new Map<string, ProductInventoryDoc>(
    (
      await Promise.all(
        chunk(inventoryIds, FIRESTORE_IN_QUERY_LIMIT).map((ids) => getProductInventories([['_id', 'in', ids]])),
      )
    )
      .flat()
      .map((inventory) => [inventory._id, inventory]),
  );
};

/** 매입단가(현재 재고 기준)와 수량으로 매입가를 계산한다. */
export const getTransactionCost = (transaction: ProductInboundTransactionDoc, inventory?: ProductInventoryDoc) =>
  inventory?.uom[transaction.inventoryUnit]?.cost ?? null;

export interface ProductSupplierReceiptRow {
  id: string;
  inventoryName: string;
  inventoryUnit: ProductInventoryUnit;
  requestValue: number;
  cost: number | null;
  totalPrice: number;
}

const fetchRows = async (transactionIds: string[]): Promise<ProductSupplierReceiptRow[]> => {
  const transactions = (
    await Promise.all(
      chunk(transactionIds, FIRESTORE_IN_QUERY_LIMIT).map((ids) => getProductInboundTransactions([['_id', 'in', ids]])),
    )
  ).flat();

  const inventoryById = await fetchInventoryById(transactions);

  const transactionById = new Map<string, ProductInboundTransactionDoc>(
    transactions.map((transaction) => [transaction._id, transaction]),
  );

  // 전달된 transactionIds 순서를 유지한다.
  return transactionIds
    .map((id) => transactionById.get(id))
    .filter((transaction): transaction is ProductInboundTransactionDoc => transaction != null)
    .map((transaction) => {
      const inventory = inventoryById.get(transaction.inventoryId);
      const cost = getTransactionCost(transaction, inventory);
      return {
        id: transaction._id,
        inventoryName: inventory?.inventoryName ?? '',
        inventoryUnit: transaction.inventoryUnit,
        requestValue: transaction.requestValue,
        cost,
        totalPrice: cost === null ? 0 : cost * transaction.requestValue,
      };
    });
};

const useSWRProductSupplierReceiptRows = (transactionIds: string[]) => {
  const key = transactionIds.length > 0 ? `productSupplierReceiptRows-${[...transactionIds].sort().join(',')}` : null;
  const { data, isLoading, error, mutate } = useSWRImmutable(key, () => fetchRows(transactionIds));

  return {
    rows: data ?? [],
    isLoading,
    error,
    refresh: () => mutate(),
  };
};

export default useSWRProductSupplierReceiptRows;
